import React, { useState, useEffect, createContext } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import EnvironmentBadge from '@/components/EnvironmentBadge';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Adolescents from '@/pages/Adolescents';
import CheckIn from '@/pages/CheckIn';
import SmallGroups from '@/pages/SmallGroups';
import Volunteers from '@/pages/Volunteers';
import Events from '@/pages/Events';
import Reports from '@/pages/Reports';
import Settings from '@/pages/Settings';
import GenderReview from '@/pages/GenderReview';
import Users from '@/pages/Users';
import Permissions from '@/pages/Permissions';
import Profile from '@/pages/Profile';
import Layout from '@/components/Layout';
import CompleteProfileModal from '@/components/CompleteProfileModal';
import { MinistryProvider } from '@/contexts/MinistryContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);

  const appName = process.env.REACT_APP_APP_NAME || 'Pulse';
  const environment = process.env.REACT_APP_ENVIRONMENT?.toUpperCase() || 'LOCAL';
  const version = process.env.REACT_APP_VERSION || '';

  useEffect(() => {
    const parts = [appName, environment];
    if (version) {
      parts.push(`v${version}`);
    }
    document.title = parts.join(' · ');
  }, [appName, environment, version]);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setPermissions(storedPermissions ? JSON.parse(storedPermissions) : []);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  const login = (token, user, perms = []) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('permissions', JSON.stringify(perms));
    setToken(token);
    setUser(user);
    setPermissions(perms);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setProfileChecked(false);
    setShowCompleteProfile(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setToken(null);
    setUser(null);
    setPermissions([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  useEffect(() => {
    if (token && user && !profileChecked) {
      setShowCompleteProfile(true);
    }
  }, [token, user, profileChecked]);

  const handleProfileComplete = () => {
    setShowCompleteProfile(false);
    setProfileChecked(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Carregando...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, token, permissions, login, logout, hasPermission }}>
      <MinistryProvider>
        <EnvironmentBadge />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!token ? <Login /> : <Navigate to="/" />} />
            <Route
              path="/"
              element={token ? <Layout /> : <Navigate to="/login" />}
            >
              <Route index element={<Dashboard />} />
              <Route path="adolescents" element={<Adolescents />} />
              <Route path="checkin" element={<CheckIn />} />
              <Route path="small-groups" element={<SmallGroups />} />
              <Route path="volunteers" element={<Volunteers />} />
              <Route path="events" element={<Events />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="gender-review" element={<GenderReview />} />
              <Route path="users" element={<Users />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Routes>
          {token && showCompleteProfile && (
            <CompleteProfileModal
              open={showCompleteProfile}
              onComplete={handleProfileComplete}
            />
          )}
        </BrowserRouter>
        <Toaster position="top-right" />
      </MinistryProvider>
    </AuthContext.Provider>
  );
}

export default App;
